import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ValidatorFn } from '@angular/forms'
import { BehaviorSubject, combineLatest, throwError } from 'rxjs'
import { finalize } from 'rxjs/operators'

import { environment } from '../../../../environments/environment'
import { Market } from '../../types/market.type'
import { ApiResponse } from '../../types/response.type'
import { PlaceBetRequest } from './types/place-bet.type'
import { SelectedRunner } from './types/selected-runner.type'

const API_URL = environment.apiUrl

@Injectable({
	providedIn: 'root',
})
export class PlaceBetModalService {
	readonly #market$ = new BehaviorSubject<Market | null>(null)

	readonly #selectedRunner$ = new BehaviorSubject<SelectedRunner | null>(null)

	readonly #additionalStakeValidators$ = new BehaviorSubject<ValidatorFn[]>([])

	readonly #isModalOpen$ = new BehaviorSubject<boolean>(false)

	readonly #currentPlacement$ = new BehaviorSubject<SelectedRunner | null>(null)

	private readonly PLACING_BET_KEY = 'placing_bet_flag'

	constructor(private readonly httpClient: HttpClient) {
		combineLatest([this.#market$, this.#selectedRunner$]).subscribe({
			next: ([market, selectedRunner]) => {
				this.#isModalOpen$.next(market !== null && selectedRunner !== null)
			},
		})
	}

	get market$() {
		return this.#market$
	}

	get selectedRunner$() {
		return this.#selectedRunner$
	}

	get additionalStakeValidators$() {
		return this.#additionalStakeValidators$
	}

	get currentPlacement$() {
		return this.#currentPlacement$
	}

	set setAdditionalStakeValidators(validators: ValidatorFn[]) {
		this.#additionalStakeValidators$.next(validators)
	}

	showPlaceBetModal(market: Market, selectedRunner: SelectedRunner, additionalStakeValidators: ValidatorFn[] = []) {
		this.#market$.next(market)
		this.#selectedRunner$.next(selectedRunner)
		this.#additionalStakeValidators$.next(additionalStakeValidators)
	}

	dismissPlaceBetModal() {
		this.#market$.next(null)
		this.#selectedRunner$.next(null)
		this.#additionalStakeValidators$.next([])
	}

	placeBet(data: PlaceBetRequest) {
		// Check cross-tab protection using localStorage
		const isPlacingInAnotherTab = localStorage.getItem(this.PLACING_BET_KEY) === 'true'

		// Global protection against concurrent bet placement (same tab + cross-tab)
		if (isPlacingInAnotherTab) {
			return throwError(() => new Error('Another bet is already processing'))
		}

		localStorage.setItem(this.PLACING_BET_KEY, 'true')

		return this.httpClient.post<ApiResponse<{ ID: number }[]>>(`${API_URL}/api/bf_placebet`, data).pipe(
			finalize(() => {
				setTimeout(() => {
					localStorage.removeItem(this.PLACING_BET_KEY)
				}, 5000)
			}),
		)
	}
}
